import { useLazyQuery } from '@apollo/client';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Checkbox,
  Heading,
  Icon,
  IconSize,
  ModalBody,
  Radio,
  TextFieldSize,
} from '@bechtel/gds';
import { OperatorType, SearchCableResult, SearchInput, TenantConfig, TermCommodityType } from '@bechtel/sr-types';
import { Formik } from 'formik';
import { get } from 'lodash';
import { FC } from 'react';
import {
  AppSpinner,
  FormField,
  FormSectionHeading,
  MultiLingualText,
  TextFieldComponent,
  useAppUser,
} from '~/components';
import { GET_CABLE_BY_TAG, SEARCH_JUMPERS } from '~/gql';
import { TermEnd } from '../types';
import {
  BoxContainerStyled,
  FooterButtonStyled,
  ListSelectionStyled,
  ModalCloseStyled,
  ModalFooterStyled,
  ModalHeaderStyled,
  ModalStyled,
} from './styled';

export interface CopyTermFormikState {
  termEnd: TermEnd;
  termCommodityType: TermCommodityType;
  isEndFlipped: boolean;
  isWireNumberCopied: boolean;
  tag: string;
  sourceId: string;
}

const initialValues: CopyTermFormikState = {
  termEnd: TermEnd.BOTH_FROM_AND_TO,
  termCommodityType: TermCommodityType.Cable,
  isEndFlipped: false,
  isWireNumberCopied: false,
  tag: '',
  sourceId: '',
};

interface CopyTermModalProps {
  commodityType: TermCommodityType;
  onCopyTerm: (value: CopyTermFormikState) => void;
  onClose: () => void;
  isLoading: boolean;
}

export const CopyTermModal: FC<CopyTermModalProps> = ({ onCopyTerm, onClose, isLoading, commodityType }) => {
  const { userAccount } = useAppUser();
  const tenantConfig: TenantConfig = get(userAccount, 'tenantConfig') as TenantConfig;
  const canCopyWireTag = !tenantConfig.isDefaultNumberingUsed;
  const isCableCommodity = commodityType === TermCommodityType.Cable;
  const [getCableDataByTag, { loading }] = useLazyQuery<
    { searchCables: SearchCableResult },
    {
      searchInput: SearchInput;
    }
  >(GET_CABLE_BY_TAG);
  const [searchJumpers, { loading: jumperLoading }] = useLazyQuery<
    { searchJumpers: SearchCableResult },
    {
      searchInput: SearchInput;
    }
  >(SEARCH_JUMPERS);
  return (
    <>
      <ModalStyled visible>
        <Formik
          initialValues={
            isCableCommodity ? { ...initialValues } : { ...initialValues, termCommodityType: TermCommodityType.Jumper }
          }
          validateOnBlur={true}
          validateOnChange={true}
          enableReinitialize={true}
          onSubmit={async (values) => {
            const search = {
              searchInput: {
                filterInput: {
                  and: [
                    {
                      field: 'tag',
                      operator: OperatorType.EQUAL,
                      value: values.tag,
                    },
                  ],
                },
              },
            };
            if (isCableCommodity) {
              const { data } = await getCableDataByTag({
                variables: search,
              });
              const sourceId = get(data, 'searchCables.items', [])[0]?.id;
              onCopyTerm({ ...values, sourceId });
            } else {
              const { data } = await searchJumpers({
                variables: search,
              });
              const sourceId = get(data, 'searchJumpers.items', [])[0]?.id;
              onCopyTerm({ ...values, sourceId });
            }
          }}
        >
          {({ values, setFieldValue, handleSubmit, dirty, touched, isValid }) => {
            return (
              <>
                <ModalHeaderStyled title="">
                  <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-evenly' }}>
                    <Box pl={{ sm: '300' }}>
                      <Heading size="500" weight="500">
                        <MultiLingualText>Copy Terminations</MultiLingualText>
                      </Heading>
                    </Box>
                    <ModalCloseStyled
                      onClick={() => {
                        onClose();
                      }}
                    >
                      <Button
                        size={ButtonSize.SMALL}
                        style={{ background: 'transparent', boxShadow: 'none' }}
                        onClick={() => close()}
                      >
                        <Icon icon="times" iconStyle="regular" size={IconSize.LARGE} />
                      </Button>
                    </ModalCloseStyled>
                  </Box>
                </ModalHeaderStyled>
                {/* @ts-ignore adjust in GDS component to take child as prop  */}
                <ModalBody
                  css={`
                    flex-direction: column;
                  `}
                >
                  <form style={{ width: '100%' }}>
                    <>
                      <Box flexGrow={{ sm: '1' }} p={{ sm: '500' }} pb={{ sm: '500' }}>
                        <FormField
                          memberName="tag"
                          pickListName={isCableCommodity ? 'CABLE_TERM_COPY' : 'JUMPER_TERM_COPY'}
                          tagValidationType="NO_DATA_FOUND"
                          required={true}
                        >
                          <TextFieldComponent />
                        </FormField>
                      </Box>
                      <Box
                        flexDirection={{ sm: 'column' }}
                        flexGrow={{ sm: '1' }}
                        py={{ sm: '100' }}
                        px={{ sm: '500' }}
                      >
                        <Box flexDirection={{ sm: 'column' }}>
                          <FormSectionHeading heading="Terminations To Be Replaced" />
                        </Box>

                        <BoxContainerStyled>
                          <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                            <Box>
                              <ListSelectionStyled onClick={() => setFieldValue('termEnd', TermEnd.BOTH_FROM_AND_TO)}>
                                <Box mr={{ sm: '500' }}>
                                  <Radio
                                    size={TextFieldSize.MEDIUM}
                                    checked={values.termEnd === TermEnd.BOTH_FROM_AND_TO}
                                    readOnly={true}
                                  />
                                </Box>
                                <MultiLingualText size="300" color="grey.900">
                                  Both FROM and TO ends
                                </MultiLingualText>
                              </ListSelectionStyled>
                            </Box>
                            <Box>
                              <ListSelectionStyled onClick={() => setFieldValue('termEnd', TermEnd.FROM_ONLY)}>
                                <Box mr={{ sm: '500' }}>
                                  <Radio
                                    size={TextFieldSize.MEDIUM}
                                    checked={values.termEnd === TermEnd.FROM_ONLY}
                                    readOnly={true}
                                  />
                                </Box>
                                <MultiLingualText size="300" color="grey.900">
                                  FROM ends only
                                </MultiLingualText>
                              </ListSelectionStyled>
                            </Box>
                            <Box>
                              <ListSelectionStyled onClick={() => setFieldValue('termEnd', TermEnd.TO_ONLY)}>
                                <Box mr={{ sm: '500' }}>
                                  <Radio
                                    size={TextFieldSize.MEDIUM}
                                    checked={values.termEnd === TermEnd.TO_ONLY}
                                    readOnly={true}
                                  />
                                </Box>
                                <MultiLingualText size="300" color="grey.900">
                                  TO ends only
                                </MultiLingualText>
                              </ListSelectionStyled>
                            </Box>
                          </Box>
                        </BoxContainerStyled>
                      </Box>
                      <Box
                        flexDirection={{ sm: 'column' }}
                        flexGrow={{ sm: '1' }}
                        py={{ sm: '100' }}
                        px={{ sm: '500' }}
                      >
                        <Box flexDirection={{ sm: 'column' }}>
                          <FormSectionHeading heading="Copy Options" />
                        </Box>
                        <BoxContainerStyled>
                          <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                            <Box>
                              <ListSelectionStyled onClick={() => setFieldValue('isEndFlipped', !values.isEndFlipped)}>
                                <Box mb={{ sm: '100' }}>
                                  <Checkbox
                                    size={TextFieldSize.MEDIUM}
                                    checked={values.isEndFlipped}
                                    readOnly={true}
                                  ></Checkbox>
                                  &nbsp;&nbsp;
                                  <MultiLingualText size="300">Reverse the ends</MultiLingualText>
                                </Box>
                              </ListSelectionStyled>
                            </Box>
                            <Box>
                              <ListSelectionStyled
                                onClick={() => {
                                  if (!canCopyWireTag) return;
                                  setFieldValue('isWireNumberCopied', !values.isWireNumberCopied);
                                }}
                              >
                                <Checkbox
                                  size={TextFieldSize.MEDIUM}
                                  checked={values.isWireNumberCopied}
                                  readOnly={true}
                                  disabled={!canCopyWireTag}
                                ></Checkbox>
                                &nbsp;&nbsp;
                                {canCopyWireTag ? (
                                  <MultiLingualText size="300">Copy wire numbers</MultiLingualText>
                                ) : (
                                  <MultiLingualText size="300" color="grey.500">
                                    Copy wire numbers
                                  </MultiLingualText>
                                )}
                              </ListSelectionStyled>
                            </Box>
                          </Box>
                        </BoxContainerStyled>
                      </Box>
                    </>
                  </form>
                </ModalBody>
                <ModalFooterStyled>
                  <Box pr={{ sm: '500' }}>
                    <FooterButtonStyled onClick={onClose} appearance={ButtonAppearance.SECONDARY}>
                      <Box px={{ sm: '500' }}>
                        <MultiLingualText>Cancel</MultiLingualText>
                      </Box>
                    </FooterButtonStyled>
                  </Box>
                  <Box>
                    <FooterButtonStyled
                      onClick={() => handleSubmit()}
                      appearance={ButtonAppearance.PRIMARY}
                      disabled={!dirty || !isValid || isLoading || loading || jumperLoading}
                    >
                      <Box px={{ sm: '500' }}>
                        {(isLoading || loading || jumperLoading) && <AppSpinner fillColor="white" />}
                        <MultiLingualText>OK</MultiLingualText>
                      </Box>
                    </FooterButtonStyled>
                  </Box>
                </ModalFooterStyled>
              </>
            );
          }}
        </Formik>
      </ModalStyled>
    </>
  );
};
